import { SupportPermissions } from "@redotech/account-common/permissions";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { isSupportTicketRouteId } from "@redotech/redo-merchant-links/links";
import { devRoutes } from "@redotech/redo-merchant-support-app/dev/route";
import { ACTIVE_CONVERSATION_QUERY_PARAMETER } from "@redotech/redo-merchant-support-app/query-parameters";
import { prefetchSupportRouteModule } from "@redotech/redo-merchant-support-app/v2/support-route-prefetch";
import { matchPath } from "react-router-dom";
import { conciergeRoutes } from "../../../concierge/route";
import { csatRoutes } from "./csat-routes";
import { faqRoutes } from "./faq-routes";

// :supportTicketId is a path-param wildcard, so /dashboard, /settings, etc.
// all match without this guard. Require the captured param to be a real ticket id.
function matchesSupportV2Route(url: string, route: string): boolean {
  const match = matchPath<"storeId" | "supportTicketId", string>(
    `/stores/:storeId/support-v2${route}`,
    url,
  );
  if (!match) {
    return false;
  }
  const ticketId = match.params.supportTicketId;
  if (ticketId !== undefined && !isSupportTicketRouteId(ticketId)) {
    return false;
  }
  return true;
}

const HIDE_PADDING_ROUTES: string[] = [
  "/table/*",
  "/dev/conversation-insights",
  "/:supportTicketId",
  "/:supportTicketId/rule-execution/:workflowId",
  "/faq/*",
  "/about",
  "/concierge/knowledge",
];

function hidePadding(url: string): boolean {
  return HIDE_PADDING_ROUTES.some((route) => matchesSupportV2Route(url, route));
}

const FULLSCREEN_ROUTES: string[] = [
  "/faq/editor/*",
  "/setup",
  "/concierge/onboard",
];

const HIDE_HEADER_ROUTES: string[] = [
  ...FULLSCREEN_ROUTES,
  "/:supportTicketId",
  "/:supportTicketId/rule-execution/:workflowId",
];
function hideHeader(url: string, { search }: { search: string }): boolean {
  if (search.includes(ACTIVE_CONVERSATION_QUERY_PARAMETER)) {
    return true;
  }

  return HIDE_HEADER_ROUTES.some((route) => matchesSupportV2Route(url, route));
}

const HIDE_NAV_ROUTES: string[] = [...FULLSCREEN_ROUTES];

function hideNav(url: string): boolean {
  return HIDE_NAV_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/support-v2${route}`, url),
  );
}

export const SUPPORT_VIEW_NAME = "Ticket view";

export const supportV2Routes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Support", breadcrumbNavigate: false },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <Page
            hideHeader={hideHeader}
            hideNavbar={hideNav}
            hidePadding={hidePadding}
          />
        ),
      };
    },
    children: [
      {
        path: "about",
        async lazy() {
          const { AboutSupport } =
            await import("@redotech/redo-merchant-support-app/about-support");
          return { element: <AboutSupport /> };
        },
      },
      {
        path: "setup",
        async lazy() {
          const { StartSupportTrial } =
            await import("@redotech/redo-merchant-support-app/support-setup/start-support-trial");
          return { element: <StartSupportTrial /> };
        },
      },
      {
        async lazy() {
          const { SupportBillingGate } =
            await import("@redotech/redo-merchant-support-app/support-billing-gate");
          return { Component: SupportBillingGate };
        },
        children: [
          { path: "faq", children: faqRoutes },
          { path: "csat", children: csatRoutes },
          { path: "concierge", children: conciergeRoutes },
          {
            async lazy() {
              const { SupportHelpdesk } =
                await import("@redotech/redo-merchant-support-app/v2/support-helpdesk");
              return { Component: SupportHelpdesk };
            },
            children: [
              {
                path: "dev",
                children: [
                  {
                    handle: { breadcrumb: "Dev", breadcrumbNavigate: false },
                    async lazy() {
                      const { RedoDevOnlyGuard } =
                        await import("@redotech/redo-merchant-support-app/dev/redo-dev-only-guard");
                      return { Component: RedoDevOnlyGuard };
                    },
                    children: [
                      {
                        path: "conversation-insights",
                        handle: { breadcrumb: "Conversation Insights" },
                        async lazy() {
                          const { ConversationInsightsPage } =
                            await import("../../../concierge/insights/conversation-insights-page");
                          return { Component: ConversationInsightsPage };
                        },
                      },
                    ],
                  },
                ],
              },
              { path: "dev", children: devRoutes },
              {
                path: "dashboard",
                handle: {
                  breadcrumb: "Dashboard",
                  permission: SupportPermissions.VIEW_ANALYTICS,
                },
                async lazy() {
                  const { ConciergeDashboardPage } =
                    await import("@redotech/redo-merchant-support-app/analytics/dashboards/concierge-dashboard-page");
                  return { Component: ConciergeDashboardPage };
                },
              },
              {
                handle: { breadcrumb: SUPPORT_VIEW_NAME },
                path: ":supportTicketId",
                children: [
                  {
                    index: true,
                    async lazy() {
                      const { Support } = await prefetchSupportRouteModule();
                      return { Component: Support };
                    },
                  },
                  {
                    path: "rule-execution/:workflowId",
                    handle: { breadcrumb: "Rule Execution" },
                    async lazy() {
                      const { RuleExecutionView } =
                        await import("@redotech/redo-merchant-support-app/v2/views/rule-execution-view");
                      return { Component: RuleExecutionView };
                    },
                  },
                ],
              },
              {
                handle: { breadcrumb: "Inbox" },
                path: "",
                async lazy() {
                  const { SupportTicketsTableView } =
                    await import("@redotech/redo-merchant-support-app/v2/views/support-tickets-table-view");
                  return { Component: SupportTicketsTableView };
                },
              },
            ],
          },
        ],
      },
    ],
  },
];
